import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useMemo, useEffect } from 'react';
import { widgetTelemetry } from '../lib/widgetTelemetry';
import { useFormikContext } from 'formik';
import { useInitialSettings } from './settings';
import { transformFormValuesToQuoteArgs, useQuoteData } from '../hooks/useFee';
import { resolveFormValidation } from '../hooks/useFormValidation';
import { useRouteValidation } from '../hooks/useRouteValidation';
import { useSwapDataState } from './swap';
import { useSelectedAccount } from './swapAccounts';
import { useSlippageStore } from '../stores';
import { useAutoSlippageTest } from '../hooks/useAutoSlippageTest';
import { useUsdModeStore } from '../stores/usdModeStore';
import { isDepositAddressFlow } from '../helpers/swapFlow';
import useExchangeNetworks from '../hooks/useExchangeNetworks';
const defaultContext = {
    formValidation: { message: '' },
    routeValidation: { message: '', details: {} },
    autoSlippageWouldWork: false,
    isTestingAutoSlippage: false,
};
const ValidationContext = createContext(defaultContext);
export const ValidationProvider = ({ children }) => {
    const { values } = useFormikContext();
    const initialSettings = useInitialSettings();
    const { sameAccountNetwork } = initialSettings;
    const { swapId } = useSwapDataState();
    const selectedSourceAccount = useSelectedAccount("from", values.from?.name);
    const quoteArgs = useMemo(() => transformFormValuesToQuoteArgs(values), [values]);
    const quoteRefreshInterval = !!swapId ? 0 : undefined;
    // Deposit address flow doesn't use limits — resolveFormValidation skips amount checks there
    const { minAllowedAmount, maxAllowedAmount, minAllowedAmountInUsd, maxAllowedAmountInUsd, quoteError, limitsError, quote, isQuoteLoading, isDebouncing } = useQuoteData(quoteArgs, { refreshInterval: quoteRefreshInterval, skipLimits: isDepositAddressFlow(values.depositMethod, values.fromExchange) });
    // Fall back to the limits error only when there are no usable limits at all, so a transient revalidation failure doesn't override a working quote
    const routeError = quoteError ?? ((minAllowedAmount == undefined && maxAllowedAmount == undefined) ? limitsError : undefined);
    const { autoSlippage } = useSlippageStore();
    const quoteErrorCode = quoteError?.response?.data?.error?.code || quoteError?.code;
    const shouldTestAutoSlippage = !autoSlippage && !quote && !!values.amount && Number(values.amount) > 0 && !!values.from && !!values.to && !quoteErrorCode && !(isQuoteLoading || isDebouncing);
    const { autoSlippageWouldWork, isTestingAutoSlippage } = useAutoSlippageTest({ values, shouldTest: shouldTestAutoSlippage });
    const { networks: exchangeWithdrawalNetworks, isLoading: exchangeNetworksLoading, isValidating: exchangeNetworksValidating } = useExchangeNetworks({ fromExchange: values.fromExchange?.name, to: values.to?.name, toAsset: values.toAsset?.symbol });
    const noExchangeWithdrawalRoute = !!values.fromExchange && !!values.to && !!values.toAsset && !exchangeNetworksLoading && !exchangeNetworksValidating && (exchangeWithdrawalNetworks?.length ?? 0) === 0;
    const routeValidation = useRouteValidation(routeError, !!quote, isQuoteLoading || isDebouncing, autoSlippageWouldWork);
    const isUsdMode = useUsdModeStore(s => s.isUsdMode);
    const formValidation = resolveFormValidation({
        values,
        maxAllowedAmount,
        minAllowedAmount,
        minAllowedAmountInUsd,
        maxAllowedAmountInUsd,
        isUsdMode,
        sourceAddress: selectedSourceAccount?.address,
        sameAccountNetwork,
        quoteError: routeError,
        noExchangeWithdrawalRoute
    });
    const validationCode = formValidation.code;
    useEffect(() => {
        widgetTelemetry.validation(validationCode);
    }, [validationCode]);
    const value = useMemo(() => ({
        formValidation,
        routeValidation,
        autoSlippageWouldWork,
        isTestingAutoSlippage,
    }), [formValidation, routeValidation, autoSlippageWouldWork, isTestingAutoSlippage]);
    return _jsx(ValidationContext.Provider, { value: value, children: children });
};
export const useValidationContext = () => React.useContext(ValidationContext);
